import AWSAccountLookupObject from '../models/AWSAccountLookupObject';
import AWSAccount from '../models/entities/AWSAccount';
import AWSPortOpeningRequest from '../models/entities/AWSPortOpeningRequests';
import ApprovalReason from '../models/interfaces/ApprovalReason';
import CancellationReason from '../models/interfaces/CancellationReason';
import RejectionModel from '../models/interfaces/RejectionModel';
import ReviewReason from '../models/interfaces/ReviewReason';
import PortOpeningRequestApproverVerification from '../models/viewModels/PortOpeningRequests/PortOpeningRequestApproverVerification';
import SubmitAWSPortOpeningRequest from '../models/viewModels/PortOpeningRequests/SubmitAWSPortOpeningRequest';
import UpdateAWSPortOpeningRequest from '../models/viewModels/PortOpeningRequests/UpdateAWSPortOpeningRequest';
import ApiClient from './ApiClient';
import { PortOpeningRequestsDirection } from '../models/enums/POR/PortOpeningRequestsDirection';
import { VirtualPrivateCloud } from '../models/entities/AzureResources/VirtualPrivateCloud';
import AWSPortOpeningRule from '../models/entities/AWSPortOpeningRule';

export default class AWSPortOpeningRequestsService extends ApiClient {
	async getAWSAccount(id: Number, includePors: boolean, abortSignal?: AbortSignal) {
		return this.get<AWSAccount>(
			`AWSPortOpeningRequests/account/${id}?includePors=${includePors.toString()}`,
			abortSignal
		);
	}

	async getAWSPORByAccountId(id: Number) {
		return this.get<AWSAccount>(`AWSPortOpeningRequests/${id}`);
	}

	async getAWSAccountWithPOR(accountId: Number, porId: Number) {
		return this.get<AWSAccount>(`AWSPortOpeningRequests/account/${accountId}/por/${porId}`);
	}

	async awsAccountLookUp(idOrName: string, abortSignal?: AbortSignal) {
		return this.get<AWSAccountLookupObject[]>(`AWSPortOpeningRequests/account/lookUp/${idOrName}`, abortSignal);
	}

	async cancel(portOpeningRequestId: number, cancellationModel: CancellationReason) {
		return this.patch<AWSPortOpeningRequest>(
			`AWSPortOpeningRequests/${portOpeningRequestId}/cancellation`,
			cancellationModel
		);
	}

	async getMyRequests() {
		return this.get<AWSPortOpeningRequest[]>('AWSPortOpeningRequests/me');
	}

	async getAWSPORRulesByFilters(
		accountId?: string,
		dbmId?: string,
		type?: string,
		direction?: string,
		secondaryAccount?: string
	) {
		const params: string[] = [];

		if (accountId) params.push(`accountId=${accountId}`);
		if (dbmId) params.push(`dbmId=${dbmId}`);
		if (type) params.push(`type=${type}`);
		if (direction) params.push(`direction=${direction}`);
		if (secondaryAccount) params.push(`secondaryAccount=${secondaryAccount}`);

		const queryString = params.length ? `?${params.join('&')}` : '';

		return this.get<AWSPortOpeningRule[]>(`AWSPortOpeningRequests/import${queryString}`);
	}

	async getMyApprovals() {
		return this.get<AWSPortOpeningRequest[]>('AWSPortOpeningRequests/myApprovals');
	}

	async getRequestById(id: number) {
		return this.get<AWSPortOpeningRequest>(`AWSPortOpeningRequests/${id}`);
	}

	async create(por: SubmitAWSPortOpeningRequest) {
		return this.post<AWSPortOpeningRequest>('AWSPortOpeningRequests', por);
	}

	async sendToReview(portOpeningRequestId: number, review: ReviewReason) {
		return this.patch<AWSPortOpeningRequest>(`AWSPortOpeningRequests/${portOpeningRequestId}/review`, review);
	}

	async reject(portOpeningRequestId: number, rejectionModel: RejectionModel) {
		return this.patch<AWSPortOpeningRequest>(
			`AWSPortOpeningRequests/${portOpeningRequestId}/rejection`,
			rejectionModel
		);
	}

	async approve(portOpeningRequestId: number, reason: ApprovalReason) {
		return this.patch<AWSPortOpeningRequest>(`AWSPortOpeningRequests/${portOpeningRequestId}/approval`, reason);
	}

	async escalate(portOpeningRequestId: number, escalationModel: ReviewReason) {
		return this.post<AWSPortOpeningRequest>(
			`AWSPortOpeningRequests/${portOpeningRequestId}/escalate`,
			escalationModel
		);
	}

	async update(portOpeningRequestId: number, por: UpdateAWSPortOpeningRequest) {
		return this.patch<AWSPortOpeningRequest>(`AWSPortOpeningRequests/${portOpeningRequestId}`, por);
	}

	async saveDraft(por: SubmitAWSPortOpeningRequest) {
		return this.put<AWSPortOpeningRequest>('AWSPortOpeningRequests/draft', por);
	}

	async verifyApproverLevel(email: string, isEmergency: boolean) {
		return this.get<PortOpeningRequestApproverVerification>(
			`AWSPortOpeningRequests/verifyApproverLevel?email=${email}&emergency=${isEmergency}`
		);
	}

	async parse(direction: PortOpeningRequestsDirection, file: File, virtualPrivateClouds: VirtualPrivateCloud[]) {
		const formData = new FormData();
		formData.append(file.name, file);
		if (virtualPrivateClouds) {
			formData.append('virtualPrivateClouds', JSON.stringify(virtualPrivateClouds.map((vpc) => vpc.id)));
		}
		return this.postFormData(`parse/awsPORRequest/${direction.name}`, formData);
	}
}
